// Remote Punch management — opened from the Attendance page action bar.
//   Header   who is remote and the rules in force right now
//   Employees switch Remote on / off per employee, with optional dates
//   Groups   switch it on for a whole group (members added later follow)
// The button owns the open state; data is only fetched while it is open.
// Built from the Shift Control style factory so it looks like the other
// Attendance dialogs.
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { MapPinned, Users, User, Search, Check, X as XIcon, Pencil, Crosshair, Eye, ShieldCheck, Inbox, CalendarRange } from "lucide-react";
import AlertModal from "../../../../components/AlertModal";
import DatePicker from "../../../../components/DatePicker";
import ReusableDropdown from "../../../../components/ReusableDropdown";
import { useProfile } from "../../../../hooks/useProfile";
import useScreenSize from "../../../../hooks/useScreenSize";
import { getTopBarButtonStyle } from "../../styles/attendanceStyles";
import { makeStyles } from "../shiftControl/shiftControlStyles";
import RemotePunchDialog, { dialogColors } from "../../../../components/RemotePunch/RemotePunchDialog";
import { remotePunchApi } from "../../../../components/RemotePunch/remotePunchApi";
import { rpText } from "../../../../components/RemotePunch/remotePunchText";

const formatDay = (iso, language) => {
  if (!iso) return "";
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(language === "ar" ? "ar-EG-u-nu-latn" : "en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/** A compact system button for inside a row. */
const rowButton = (s, variant = "ghost", disabled = false) => ({ ...s.btn(variant, disabled), minHeight: 38, padding: "8px 14px" });

/** Status badge + Edit: one unit that sits at the row end, also when it wraps. */
const rowEnd = { display: "flex", alignItems: "center", gap: 10, marginInlineStart: "auto" };

const initialsOf = (name) =>
  String(name || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

const Avatar = ({ name, on, c }) => (
  <div style={{
    width: 38, height: 38, flexShrink: 0, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 13, fontWeight: 800, color: on ? c.primary : c.muted,
    background: on ? `${c.primary}1f` : c.soft, border: `1px solid ${on ? `${c.primary}40` : c.border}`,
  }}>
    {initialsOf(name)}
  </div>
);

const rowStyle = (c, on) => ({
  padding: "12px 16px", borderRadius: 14,
  border: `1px solid ${on ? `${c.primary}55` : c.border}`,
  background: on ? `${c.primary}0f` : c.soft,
});

const RowSkeleton = ({ c }) => (
  <div style={{ ...rowStyle(c, false), display: "flex", alignItems: "center", gap: 12 }}>
    <div style={{ width: 38, height: 38, borderRadius: 12, background: c.border }} />
    <div style={{ flex: 1 }}>
      <div style={{ width: "45%", height: 12, borderRadius: 6, background: c.border, marginBottom: 8 }} />
      <div style={{ width: "25%", height: 10, borderRadius: 6, background: c.border }} />
    </div>
    <div style={{ width: 70, height: 26, borderRadius: 999, background: c.border }} />
  </div>
);

/** Inline editor shared by an employee row and a group row. */
function RemoteEditor({ initial, c, s, t, theme, language, onSave, onCancel, saving }) {
  const [enabled, setEnabled] = useState(Boolean(initial?.enabled));
  const [startDate, setStartDate] = useState(initial?.startDate || "");
  const [endDate, setEndDate] = useState(initial?.endDate || "");
  const invalid = enabled && startDate && endDate && endDate < startDate;
  const isRTL = language === "ar";
  const m = s.isMobile;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: m ? 12 : 16, marginTop: 12, borderRadius: 12, background: c.panel, border: `1px solid ${c.border}` }}>
      {/* On / off */}
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={() => setEnabled((v) => !v)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%", padding: 0, border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", color: c.text, textAlign: "start" }}
      >
        <span style={{ minWidth: 0 }}>
          <span style={{ display: "block", fontWeight: 700, fontSize: 14 }}>{t.allowRemote}</span>
          <span style={{ display: "block", fontSize: 12.5, color: c.muted, marginTop: 2 }}>{enabled ? t.allowRemoteHint : t.notRemote}</span>
        </span>
        <span style={{ position: "relative", width: 44, height: 24, borderRadius: 999, flexShrink: 0, background: enabled ? c.primary : (c.dark ? "#475569" : "#cbd5e1"), transition: "background .15s" }}>
          <span style={{ position: "absolute", top: 3, insetInlineStart: enabled ? 23 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.3)", transition: "inset-inline-start .15s" }} />
        </span>
      </button>

      {enabled && (
        <div style={{ display: "grid", gridTemplateColumns: m ? "1fr" : "1fr 1fr", gap: 12 }}>
          <div style={s.field("1 1 auto", 0)}>
            <span style={s.fieldLabel}><CalendarRange size={13} style={{ verticalAlign: "-2px" }} /> {t.from}</span>
            <DatePicker theme={theme} isRTL={isRTL} value={startDate} onChange={setStartDate} placeholder={t.from} style={{ width: "100%" }} />
          </div>
          <div style={s.field("1 1 auto", 0)}>
            <span style={s.fieldLabel}>{t.to}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <DatePicker theme={theme} isRTL={isRTL} value={endDate} minDate={startDate || undefined} onChange={setEndDate} placeholder={t.noEnd} style={{ width: "100%" }} />
              {endDate && (
                <button type="button" className="sc-close" style={s.iconBtn()} onClick={() => setEndDate("")} aria-label={t.noEnd} title={t.noEnd}>
                  <XIcon size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {invalid && <div style={{ fontSize: 12, color: c.danger }}>{t.dateRangeInvalid}</div>}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="button" className="sc-ghost" style={{ ...s.btn("ghost", saving), ...(m && { flex: "1 1 0" }) }} onClick={onCancel} disabled={saving}>
          <XIcon size={15} /> {t.cancel}
        </button>
        <button
          type="button"
          className="sc-primary"
          style={{ ...s.btn("primary", saving || invalid), ...(m && { flex: "1 1 0" }) }}
          disabled={saving || invalid}
          onClick={() => onSave({ enabled, startDate: enabled ? startDate || null : null, endDate: enabled ? endDate || null : null })}
        >
          <Check size={15} /> {saving ? t.saving : t.save}
        </button>
      </div>
    </div>
  );
}

const StatusBadge = ({ remote, c, t }) => {
  const on = remote?.enabled;
  const active = on && remote.activeToday !== false;
  const color = active ? c.success : on ? c.warning : c.muted;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, color, background: `${color}1a`, whiteSpace: "nowrap" }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: color }} />
      {on ? (active ? t.remote : t.inactiveToday) : t.notRemote}
    </span>
  );
};

const dateRangeText = (remote, t, language) => {
  if (!remote?.enabled) return null;
  if (!remote.startDate && !remote.endDate) return t.noEnd;
  const arrow = language === "ar" ? "←" : "→";
  return `${remote.startDate ? formatDay(remote.startDate, language) : "…"} ${arrow} ${remote.endDate ? formatDay(remote.endDate, language) : t.noEnd}`;
};

/** Who is remote and which rules apply — always visible above the tabs. */
function Summary({ overview, c, t, language, isMobile }) {
  const cols = isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))";
  if (!overview) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: cols, gap: 8, marginBottom: 14 }}>
        {[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 64, borderRadius: 12, background: c.soft, border: `1px solid ${c.border}` }} />)}
      </div>
    );
  }
  const mode = !overview.monitoringStartedAt
    ? { Icon: Eye, color: c.warning, value: t.modePending }
    : overview.monitoring
      ? { Icon: Eye, color: c.warning, value: t.modeMonitoring(formatDay(overview.monitoringEndsAt, language)) }
      : { Icon: ShieldCheck, color: c.danger, value: t.modeEnforcing };
  const tiles = [
    { Icon: MapPinned, color: c.primary, label: t.tileRemote, value: overview.remote, big: true },
    { Icon: Check, color: c.success, label: t.tileActive, value: overview.activeToday, big: true },
    { Icon: Crosshair, color: c.muted, label: t.tileAccuracy, value: `± ${overview.accuracyThresholdMeters} ${t.meters}`, big: true },
    { ...mode, label: t.tileMode },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: cols, gap: 8, marginBottom: 14 }}>
      {tiles.map(({ Icon, color, label, value, big }) => (
        <div key={label} style={{ padding: "10px 12px", borderRadius: 12, background: c.soft, border: `1px solid ${c.border}`, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: c.muted }}>
            <Icon size={14} color={color} style={{ flexShrink: 0 }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
          </div>
          <div style={{ marginTop: 4, fontWeight: 800, color: c.text, fontSize: big ? 18 : 13, lineHeight: 1.35 }} dir={big ? "ltr" : undefined}>
            {value}
          </div>
        </div>
      ))}
    </div>
  );
}

/** One labelled detail under an employee / group row. */
const Detail = ({ label, children, color, c }) => (
  <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6, minWidth: 0 }}>
    <span style={{ color: c.muted }}>{label}:</span>
    <span style={{ fontWeight: 600, color: color || c.text }}>{children}</span>
  </span>
);

function EmployeesTab({ c, s, t, theme, language, clientName, notify, onChanged }) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [onlyRemote, setOnlyRemote] = useState(false);
  const [company, setCompany] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [meta, setMeta] = useState({ companies: [], departments: [] });
  const [data, setData] = useState({ employees: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    remotePunchApi.meta({ clientName })
      .then((m) => setMeta({ companies: m?.companies || [], departments: m?.departments || [] }))
      .catch(notify);
  }, [clientName, notify]);

  // Departments of the chosen company only.
  const departments = useMemo(
    () => meta.departments.filter((d) => !company || d.workingCompany === company),
    [meta.departments, company],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await remotePunchApi.listEmployees({
        search: query,
        onlyRemote: onlyRemote ? "true" : "",
        workingCompany: company,
        departmentId,
        clientName,
      });
      setData(Array.isArray(res) ? { employees: res, total: res.length } : res);
    } catch (err) {
      notify(err);
    } finally {
      setLoading(false);
    }
  }, [query, onlyRemote, company, departmentId, clientName, notify]);

  useEffect(() => { load(); }, [load]);

  const save = async (code, body) => {
    setSaving(true);
    try {
      await remotePunchApi.setEmployee(code, body, clientName);
      setEditing(null);
      await load();
      onChanged();
    } catch (err) {
      notify(err);
    } finally {
      setSaving(false);
    }
  };

  const rows = data.employees || [];
  return (
    <div>
      <div style={s.toolbar}>
        <div style={{ flex: "1 1 180px", minWidth: 0 }}>
          <ReusableDropdown
            items={[{ key: "", label: t.allCompanies }, ...meta.companies.map((name) => ({ key: name, label: name }))]}
            value={company}
            onSelect={(item) => { setCompany(item.key); setDepartmentId(""); }}
            placeholder={t.allCompanies}
            getItemKey={(it) => it.key}
            getItemLabel={(it) => it.label}
            searchable={false}
            maxItems={100}
            theme={theme}
            language={language}
            ariaLabel={t.allCompanies}
          />
        </div>
        <div style={{ flex: "1 1 200px", minWidth: 0 }}>
          <ReusableDropdown
            key={`department-${company}`}
            items={[
              { key: "", label: t.allDepartments },
              ...departments.map((d) => ({
                key: String(d.id),
                label: `${(language === "ar" && d.nameAr) || d.name}${company ? "" : ` · ${d.workingCompany}`}`,
              })),
            ]}
            value={departmentId}
            onSelect={(item) => setDepartmentId(item.key)}
            placeholder={t.allDepartments}
            getItemKey={(it) => it.key}
            getItemLabel={(it) => it.label}
            maxItems={200}
            theme={theme}
            language={language}
            ariaLabel={t.allDepartments}
          />
        </div>
        <div style={{ position: "relative", flex: s.isMobile ? "1 1 100%" : "1 1 200px", minWidth: 0 }}>
          <Search size={16} style={{ position: "absolute", insetInlineStart: 13, top: "50%", transform: "translateY(-50%)", color: c.muted, pointerEvents: "none" }} />
          <input
            className="sc-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.search}
            style={{ ...s.input, width: "100%", paddingInlineStart: 38 }}
          />
        </div>
        <label style={s.checkRow}>
          <input type="checkbox" className="sc-check" style={s.checkbox} checked={onlyRemote} onChange={(e) => setOnlyRemote(e.target.checked)} />
          {t.onlyRemote}
        </label>
      </div>

      {loading && !rows.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[0, 1, 2, 3, 4].map((i) => <RowSkeleton key={i} c={c} />)}
        </div>
      ) : !rows.length ? (
        <div style={{ padding: 32, textAlign: "center", color: c.muted }}>
          <Inbox size={36} style={{ opacity: 0.5, marginBottom: 8 }} />
          <div style={{ fontWeight: 600 }}>{t.noEmployees}</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, opacity: loading ? 0.6 : 1, transition: "opacity .15s" }}>
          {rows.map((r) => {
            const name = (language === "ar" ? r.nameAr || r.name : r.name || r.nameAr) || r.infinityCode;
            const range = dateRangeText(r.remote, t, language);
            const on = Boolean(r.remote?.enabled);
            return (
              <div key={r.infinityCode} style={rowStyle(c, on)}>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
                  <Avatar name={name} on={on} c={c} />
                  <div style={{ flex: "1 1 160px", minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                    <div style={{ fontSize: 12, color: c.muted, marginTop: 1 }}>
                      {[r.clientCode || r.infinityCode, r.workingCompany].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div style={{ ...rowEnd, ...(s.isMobile && { width: "100%", justifyContent: "space-between", marginInlineStart: 0 }) }}>
                    <StatusBadge remote={r.remote} c={c} t={t} />
                    {editing !== r.infinityCode && (
                      <button type="button" className="sc-ghost" style={rowButton(s)} onClick={() => setEditing(r.infinityCode)}>
                        <Pencil size={13} /> {t.edit}
                      </button>
                    )}
                  </div>
                </div>
                {on && (
                  <div style={{ display: "flex", flexDirection: s.isMobile ? "column" : "row", flexWrap: "wrap", gap: s.isMobile ? 4 : "4px 18px", marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${c.border}`, fontSize: 12.5 }}>
                    {range && <Detail c={c} label={t.periodLabel}>{range}</Detail>}
                    <Detail c={c} label={t.source}>{r.remote.source === "GROUP" ? t.sourceGroup(r.remote.groupName) : t.sourceManual}</Detail>
                    <Detail c={c} label={t.consentLabel} color={r.remote.consentAccepted ? c.success : c.warning}>
                      {r.remote.consentAccepted ? `✓ ${t.consentDone}` : t.consentPending}
                    </Detail>
                  </div>
                )}
                {editing === r.infinityCode && (
                  <RemoteEditor
                    initial={on ? r.remote : { enabled: true }}
                    c={c}
                    s={s}
                    theme={theme}
                    language={language}
                    t={t}
                    saving={saving}
                    onCancel={() => setEditing(null)}
                    onSave={(body) => save(r.infinityCode, body)}
                  />
                )}
              </div>
            );
          })}
          {data.total > rows.length && (
            <div style={{ fontSize: 12.5, color: c.muted, textAlign: "center", padding: "6px 0" }}>{t.showingFirst(rows.length)}</div>
          )}
        </div>
      )}
    </div>
  );
}

function GroupsTab({ c, s, t, theme, language, clientName, notify, notifySuccess, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await remotePunchApi.listGroups({ clientName }));
    } catch (err) {
      notify(err);
    } finally {
      setLoading(false);
    }
  }, [clientName, notify]);

  useEffect(() => { load(); }, [load]);

  const save = async (groupId, body) => {
    setSaving(true);
    try {
      const result = await remotePunchApi.setGroup(groupId, body, clientName);
      setEditing(null);
      await load();
      onChanged();
      notifySuccess(body.enabled ? t.groupApplied(result?.applied ?? 0) : t.groupReleased(result?.released ?? 0));
    } catch (err) {
      notify(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 12.5, color: c.muted, marginBottom: 12, lineHeight: 1.6, padding: "10px 12px", borderRadius: 12, background: c.soft, border: `1px solid ${c.border}` }}>
        <Users size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>{t.groupHint}</span>
      </div>
      {loading && !rows.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[0, 1, 2].map((i) => <RowSkeleton key={i} c={c} />)}
        </div>
      ) : !rows.length ? (
        <div style={{ padding: 32, textAlign: "center", color: c.muted }}>
          <Inbox size={36} style={{ opacity: 0.5, marginBottom: 8 }} />
          <div style={{ fontWeight: 700, color: c.text }}>{t.noGroups}</div>
          <div style={{ fontSize: 12.5, marginTop: 4 }}>{t.noGroupsHint}</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((g) => {
            const remote = g.remote ? { ...g.remote, activeToday: true } : null;
            const on = Boolean(remote?.enabled);
            const range = dateRangeText(remote, t, language);
            return (
              <div key={g.groupId} style={rowStyle(c, on)}>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 38, height: 38, flexShrink: 0, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: on ? c.primary : c.muted, background: on ? `${c.primary}1f` : c.soft, border: `1px solid ${on ? `${c.primary}40` : c.border}` }}>
                    <Users size={17} />
                  </div>
                  <div style={{ flex: "1 1 160px", minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.groupName}</div>
                    <div style={{ fontSize: 12, color: c.muted }}>{g.memberCount} {t.members}</div>
                  </div>
                  <div style={{ ...rowEnd, ...(s.isMobile && { width: "100%", justifyContent: "space-between", marginInlineStart: 0 }) }}>
                    <StatusBadge remote={remote} c={c} t={t} />
                    {editing !== g.groupId && (
                      <button type="button" className="sc-ghost" style={rowButton(s)} onClick={() => setEditing(g.groupId)}>
                        <Pencil size={13} /> {t.edit}
                      </button>
                    )}
                  </div>
                </div>
                {on && range && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${c.border}`, fontSize: 12.5 }}>
                    <Detail c={c} label={t.periodLabel}>{range}</Detail>
                  </div>
                )}
                {editing === g.groupId && (
                  <RemoteEditor
                    initial={on ? remote : { enabled: true }}
                    c={c}
                    s={s}
                    theme={theme}
                    language={language}
                    t={t}
                    saving={saving}
                    onCancel={() => setEditing(null)}
                    onSave={(body) => save(g.groupId, body)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RemotePunchContent({ theme, language, onClose }) {
  const t = rpText(language);
  const dark = theme === "dark";
  const c = dialogColors(dark);
  const { isMobile } = useScreenSize();
  const s = useMemo(() => makeStyles(theme, language === "ar", isMobile), [theme, language, isMobile]);
  const { profile } = useProfile();
  const clientName = profile?.clientName || localStorage.getItem("clientName") || "";
  const [tab, setTab] = useState("employees");
  const [overview, setOverview] = useState(null);
  const [alert, setAlert] = useState({ open: false, title: "", message: "", variant: "info" });

  const notify = useCallback(
    (err) => setAlert({ open: true, title: t.error, message: err?.message || String(err), variant: "error" }),
    [t.error],
  );
  const notifySuccess = useCallback(
    (message) => setAlert({ open: true, title: t.success, message, variant: "success" }),
    [t.success],
  );

  const loadOverview = useCallback(() => {
    remotePunchApi.overview({ clientName }).then(setOverview).catch(() => {});
  }, [clientName]);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const tabs = useMemo(() => [
    { key: "employees", label: t.tabEmployees, Icon: User },
    { key: "groups", label: t.tabGroups, Icon: Users },
  ], [t]);

  return (
    <RemotePunchDialog
      onClose={onClose}
      title={t.manageTitle}
      subtitle={t.manageSubtitle}
      icon={MapPinned}
      dark={dark}
      language={language}
      maxWidth={860}
      zIndex={9000}
      closeLabel={t.close}
    >
      <AlertModal
        open={alert.open}
        title={alert.title}
        message={alert.message}
        variant={alert.variant}
        onConfirm={() => setAlert((a) => ({ ...a, open: false }))}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
        theme={theme}
        language={language}
      />
      <Summary overview={overview} c={c} t={t} language={language} isMobile={isMobile} />
      <div role="tablist" style={{ ...s.tabs, marginBottom: 14 }}>
        {tabs.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            style={{ ...s.tab(tab === key), fontFamily: "inherit" }}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>
      <div style={{ minHeight: 320 }}>
        {tab === "employees" ? (
          <EmployeesTab c={c} s={s} t={t} theme={theme} language={language} clientName={clientName} notify={notify} onChanged={loadOverview} />
        ) : (
          <GroupsTab c={c} s={s} t={t} theme={theme} language={language} clientName={clientName} notify={notify} notifySuccess={notifySuccess} onChanged={loadOverview} />
        )}
      </div>
    </RemotePunchDialog>
  );
}

/**
 * Action-bar button that opens Remote Punch management. `hideTrigger` drops
 * the button; the phone action menu opens it through the ref.
 */
const RemotePunchModal = forwardRef(function RemotePunchModal({ theme, language, hideTrigger = false }, ref) {
  const [open, setOpen] = useState(false);
  const t = rpText(language);
  useImperativeHandle(ref, () => ({ open: () => setOpen(true) }), []);

  return (
    <>
      {!hideTrigger && (
        <button
          onClick={() => setOpen(true)}
          style={getTopBarButtonStyle(theme, false)}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)";
          }}
        >
          <MapPinned size={16} />
          {t.manageButton}
        </button>
      )}
      {open && <RemotePunchContent theme={theme} language={language} onClose={() => setOpen(false)} />}
    </>
  );
});

export default RemotePunchModal;
